import React, { useState, useEffect, useMemo, useContext } from "react";
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import "components/sidebarComponents/SidebarModule/SidebarModule.scss";
import "components/sidebarComponents/SidebarAdvancedFilters/SidebarAdvancedFilters.scss";

import Accordion from "components/uiComponents/Accordion";
import ToggleSwitch from "components/uiComponents/ToggleSwitch";
import AdvanceFiltersContent from "../AdvanceFiltersContent/AdvanceFiltersContent";
import UniversalButton from "components/uiComponents/UniversalButton";
import AddParameterModal from "components/modals/AddParameterModal/AddParameterModal";
import { ModalContext } from "components/ModalContext";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { useConditionModal } from "components/DashboardInfoTableV2/model/use-condition-modal";

import { updateFilterTable } from "store/actions/templateSettings";
import { ItemsTypeEnum, initTemporarySetting } from "store/reducers/viewerReducer";
import { getTableColumns } from "store/selectors/templateSettings";
import { createSettingColorSchemeAC } from "store/actions/tabsSettings";

import { getTabs } from "store/selectors/tabsSettings";
import useSetting from "../SidebarProject/use-settings";
import { ColorSidebarMainContentSetting } from "../ColorSidebar/ColorSidebarMainContent";

import { clearColors } from "utils/builder/IScheduleLogic";
import { getColumnType } from "utils/helpers";

import { ReactComponent as BrushIcon } from "static/icons/brush.svg";
import { ReactComponent as FilterIcon } from "static/icons/filter.svg";
import { ReactComponent as CloseIcon } from "static/icons/close-small.svg";
import { ReactComponent as EyeIcon } from "static/icons/eye.svg";
import { ReactComponent as BinIcon } from "static/icons/bin.svg";
import { ReactComponent as FunctionIcon } from "static/icons/function.svg";
import { ReactComponent as CustomColumnsIcon } from "static/icons/custom-columns.svg";
import { valueTypes } from "utils/builder/ResultClasses";

const SidebarAdvancedFilters = ({ sideBarModuleOpened }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { handleModal } = useContext(ModalContext);

  const { temporarySetting, setHiddenColumnHandler, clearItems, openSideBarHandler, closeAllSettings, saveTemporaryColumn, setPrimaryColor } = useSetting();

  const { metaData, type } = temporarySetting;

  const tableColumns = useSelector(getTableColumns)

  const { isPrimaryColor, currentOpenTab, isCustomColumn } = metaData;

  const currentColumnId = metaData.name;
  const currentTableId = 1;

  const [isApplyClicked, setIsApplyClicked] = useState(false);

  const isCustomType = type >= valueTypes.customNumber && type <= valueTypes.customEnumMulty;
  const isProType = type >= valueTypes.proNumber && type <= valueTypes.proEnumMulty;

  useEffect(() => {
    if (isApplyClicked) {
      onCloseHandler();
    }
  }, [isApplyClicked])

  useEffect(() => {
    if (!sideBarModuleOpened) {
      closeAllSettings();
    }
  }, [sideBarModuleOpened])

  const originColumn = useMemo(() => {
    return tableColumns.find((column) => column.metaData.name === currentColumnId) ?? initTemporarySetting
  }, [currentColumnId, tableColumns])

  const currentColorsItems = useMemo(() => originColumn.settingsItems?.colors ?? [], [originColumn]);
  const currentFiltersItems = useMemo(() => originColumn.settingsItems?.filters ?? [], [originColumn]);

  const currentGradientValue = useMemo(() => originColumn.settingsItems?.gradientData ?? {
    firstValue: '#000',
    secondValue: '#000'
  }, [originColumn]);

  const currentColorsItemsCount = currentColorsItems?.length ?? 0;
  const currentFiltersItemsCount = currentFiltersItems?.length ?? 0;

  const currentColumnType = getColumnType(type);

  const columnType = currentColumnType;

  useEffect(() => {

    clearColors()

    if (currentColumnId) {
      dispatch(
        createSettingColorSchemeAC({
          columnId: currentColumnId,
          colorSchemeType: currentColumnType,
          isPrimaryColumn: isPrimaryColor,
          colorColumn: currentColumnId,
          currentOpenTabId: currentOpenTab,
          colorColumnType: columnType,
          colorItems: currentColorsItems,
          gradientData: currentGradientValue,
          currentTableId,
        })
      );
    };

    setIsApplyClicked(false)
  }, [originColumn])

  const setIsHiddenColumnHandler = () => {
    setHiddenColumnHandler();
  };

  const setIsPrimaryColorColumnHandler = () => {
    setPrimaryColor()
  };

  const onCloseHandler = () => {
    openSideBarHandler(currentColumnId)
  };

  const onClearHandler = (types) => {
    clearItems(types);
  };

  const clearAllHandler = () => {
    onClearHandler([ItemsTypeEnum.filters, ItemsTypeEnum.colors]);
  };

  const clearFilters = (event) => {
    event.stopPropagation();
    onClearHandler(ItemsTypeEnum.filters);
  };

  const clearColorsSettings = (event) => {
    event.stopPropagation();
    onClearHandler(ItemsTypeEnum.colors);
  };

  const currentTabSettings = useSelector(getTabs)?.[currentTableId];

  const onShowConditionModal = useConditionModal({
    tableId: currentTableId,
  });

  const handleEditFunction = () => {
    onShowConditionModal({
      columnScheme: currentTabSettings?.columnSchemes?.[currentColumnId],
      isEdit: true,
      autoActiveInputs: true,
    });
  };

  const handleCloseModal = () => {
    handleModal(null, true);
  };

  const showColumnDeletePopUp = (columnName, onDeleteHandler) => {
    const deleteCallback = () => {
      onDeleteHandler();
      onCloseHandler();
    };

    handleModal(
      <ConfirmActionModal
        title={t('delete_parameter')}
        description={
          <>
            {t('delete_the_column')} <span>{columnName}</span>?
          </>
        }
        confirmButtonText={t('yes_delete')}
        confirmCallback={deleteCallback}
        onClose={handleCloseModal}
      />
    );
  };

  const handleEditCustomProperty = () => {
    if (isProType) {
      return;
    }
    handleModal(
      <AddParameterModal
        key={`edit-${originColumn?.metaData?.name}-${originColumn?.type}`}
        isEdit={true}
        originColumn={originColumn}
        deleteCallback={showColumnDeletePopUp}
        closeModal={handleCloseModal}
        onCloseHandler={onCloseHandler}
      />,
      false,
      true
    );
  };

  const applyTemporarySettings = () => {
    saveTemporaryColumn(currentTableId)
    dispatch(updateFilterTable(false, setIsApplyClicked(true)))
  };

  return (
    <div
      className={classNames(
        "sidebar-module",
        "sidebar-advanced-filters-wrapper",
        "sidebar-module--right",
        {
          "sidebar-module--closed": !metaData.isSidebarOpen,
        }
      )}
    >
      <div>
        <div className="scroll-wrapper">
          <div className="sidebar-module__header sidebar-module__header--space-between">
            <div className="sidebar-module__header_title">{t('column_settings')}</div>

            <div
              className="sidebar-module__close-block"
              onClick={onCloseHandler}
            >
              <CloseIcon className="sidebar-module__header_close-icon" />
            </div>
          </div>

          <div className="sidebar-module__content">
            <div className="sidebar-advanced-filters__content">
              <div className="sidebar-advanced-filters__show-column">
                <div className="sidebar-advanced-filters__group">
                  <div className="sidebar-advanced-filters__group__title">
                    <EyeIcon />
                    {t('show_column')}
                  </div>

                  <div>
                    <ToggleSwitch
                      small
                      onChange={setIsHiddenColumnHandler}
                      checked={!metaData?.isHiddenColumn}
                      id="column-primary-color"
                    />
                  </div>
                </div>
              </div>
              {isCustomColumn ? (
                <div className="sidebar-advanced-filters__group">
                  <div className="sidebar-advanced-filters__group__title">
                    <FunctionIcon color="#63666A" />
                    {t('function')}
                  </div>
                  <div onClick={handleEditFunction}>
                    <span className="sidebar-advanced-filters__group_edit">
                      {t('edit')}
                    </span>
                  </div>
                </div>
              ) : null}

              {isCustomType ? (
                <div className="sidebar-advanced-filters__group">
                  <div className="sidebar-advanced-filters__group__title">
                    <CustomColumnsIcon color="#63666A" />
                    {t('parameter_properties')}
                  </div>
                  <div onClick={handleEditCustomProperty}>
                    <span className="sidebar-advanced-filters__group_edit">
                      {t('edit')}
                    </span>
                  </div>
                </div>
              ) : null}

              {isCustomColumn ? null : (
                <Accordion
                  headerProps={{
                    title: (
                      <span className="accordion-title">
                        {t('filters')} ({currentFiltersItemsCount})
                      </span>
                    ),
                    icon: <FilterIcon />,
                    clearHandler: clearFilters,
                  }}
                >
                  <AdvanceFiltersContent />
                </Accordion>
              )}

              <Accordion
                headerProps={{
                  title: (
                    <span className="accordion-title">
                      {t('coloring')} ({currentColorsItemsCount})
                    </span>
                  ),
                  icon: <BrushIcon color="#63666A" />,
                  clearHandler: clearColorsSettings,
                }}
              >
                <div className="color_content_wrapper">
                  <div className="sidebar-advanced-filters__group">
                    <div className="sidebar-advanced-filters__group__title">
                      {t('primary_highlight_color')}
                    </div>
                    <div>
                      <ToggleSwitch
                        small
                        onChange={setIsPrimaryColorColumnHandler}
                        checked={isPrimaryColor}
                        id="hidden-column"
                      />
                    </div>
                  </div>
                  <ColorSidebarMainContentSetting />
                </div>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-btns-container">
        <UniversalButton
          onClick={clearAllHandler}
          labelColor="blue"
          leftIcon={<BinIcon />}
          backgroundHoverColor={"blue-light"}
          backgroundColor={"blue"}
          isFullWidth
          label={t('clear')}
        />
        <UniversalButton
          onClick={applyTemporarySettings}
          labelColor="white"
          backgroundHoverColor={"orange-light"}
          isFullWidth
          backgroundColor={"orange"}
          label={t('apply')}
        />
      </div>
    </div>
  );
};

export default SidebarAdvancedFilters;
